import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  CustomReportRow,
  SavedViewRow,
  ScheduledReportRow,
  ScheduledReportViewTarget,
} from '@constants/Reports/presetsAutomation.constants'

export interface ReportsState {
  customReports: CustomReportRow[]
  scheduledReports: ScheduledReportRow[]
  savedViews: SavedViewRow[]
}

const initialState: ReportsState = {
  customReports: [],
  scheduledReports: [],
  savedViews: [],
}

export type AddCustomReportPayload = {
  name: string
  savedBy?: string
  categoryIds: string[]
  metricIds: string[]
  toolbarSelection: Record<string, string>
  headerBrandEnabled: boolean
}

export type AddScheduledReportPayload = {
  reportName: string
  summaryLine: string
  savedBy?: string
  viewTarget?: ScheduledReportViewTarget
}

export type AddSavedViewPayload = {
  name: string
  previewTitle: string
  savedBy?: string
  categoryIds: string[]
  metricIds: string[]
  toolbarSelection: Record<string, string>
  headerBrandEnabled: boolean
  bundleView?: SavedViewRow['bundleView']
}

function formatDateLabel(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatScheduleDateShort(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

function generateCustomReportId(): string {
  const random = Math.random().toString(36).slice(2, 8)
  return `cr-${Date.now().toString(36)}-${random}`
}

function generateScheduledReportId(): string {
  const random = Math.random().toString(36).slice(2, 8)
  return `sch-${Date.now().toString(36)}-${random}`
}

function generateUserSavedViewId(): string {
  const random = Math.random().toString(36).slice(2, 8)
  return `svu-${Date.now().toString(36)}-${random}`
}

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    addCustomReport(
      state: ReportsState,
      action: PayloadAction<AddCustomReportPayload>,
    ): void {
      const now = new Date()
      const dateLabel = formatDateLabel(now)
      const row: CustomReportRow = {
        id: generateCustomReportId(),
        name: action.payload.name.trim() || 'Untitled Custom Report',
        dateSaved: dateLabel,
        lastUpdated: dateLabel,
        savedBy: action.payload.savedBy?.trim() || 'You',
        categoryIds: action.payload.categoryIds,
        metricIds: action.payload.metricIds,
        toolbarSelection: { ...action.payload.toolbarSelection },
        headerBrandEnabled: action.payload.headerBrandEnabled,
      }
      state.customReports.unshift(row)
    },
    removeCustomReport(
      state: ReportsState,
      action: PayloadAction<string>,
    ): void {
      state.customReports = state.customReports.filter(
        (r) => r.id !== action.payload,
      )
    },
    clearCustomReports(state: ReportsState): void {
      state.customReports = []
    },
    addScheduledReport(
      state: ReportsState,
      action: PayloadAction<AddScheduledReportPayload>,
    ): void {
      const now = new Date()
      const dateLabel = formatDateLabel(now)
      const scheduleDay = formatScheduleDateShort(now)
      const row: ScheduledReportRow = {
        id: generateScheduledReportId(),
        name: action.payload.reportName.trim() || 'Report',
        summaryLine: action.payload.summaryLine,
        dateSaved: dateLabel,
        lastUpdated: dateLabel,
        savedBy: action.payload.savedBy?.trim() || 'You',
        scheduled: true,
        scheduleDate: scheduleDay,
        dateAdded: dateLabel,
        viewTarget: action.payload.viewTarget,
      }
      state.scheduledReports.unshift(row)
    },
    removeScheduledReport(
      state: ReportsState,
      action: PayloadAction<string>,
    ): void {
      state.scheduledReports = state.scheduledReports.filter(
        (r) => r.id !== action.payload,
      )
    },
    /**
     * Upsert a scheduled report by id (Presets edit modal; copies sample rows into Redux).
     */
    updateScheduledReport(
      state: ReportsState,
      action: PayloadAction<ScheduledReportRow>,
    ): void {
      const dateLabel = formatDateLabel(new Date())
      const scheduleDay = formatScheduleDateShort(new Date())
      const next: ScheduledReportRow = {
        ...action.payload,
        name: action.payload.name.trim() || 'Report',
        lastUpdated: dateLabel,
        scheduleDate: action.payload.scheduleDate || scheduleDay,
        scheduled: true,
        toolbarSelection: action.payload.toolbarSelection
          ? { ...action.payload.toolbarSelection }
          : undefined,
        viewTarget: action.payload.viewTarget
          ? { ...action.payload.viewTarget }
          : undefined,
      }
      const idx = state.scheduledReports.findIndex((r) => r.id === next.id)
      if (idx >= 0) {
        state.scheduledReports[idx] = next
      } else {
        state.scheduledReports.unshift(next)
      }
    },
    addSavedView(
      state: ReportsState,
      action: PayloadAction<AddSavedViewPayload>,
    ): void {
      const now = new Date()
      const dateLabel = formatDateLabel(now)
      const row: SavedViewRow = {
        id: generateUserSavedViewId(),
        name: action.payload.name.trim() || 'Untitled saved view',
        previewTitle: action.payload.previewTitle.trim() || 'Report',
        dateSaved: dateLabel,
        lastUpdated: dateLabel,
        savedBy: action.payload.savedBy?.trim() || 'You',
        preset: {
          categoryIds: [...action.payload.categoryIds],
          metricIds: [...action.payload.metricIds],
          toolbarSelection: { ...action.payload.toolbarSelection },
          headerBrandEnabled: action.payload.headerBrandEnabled,
        },
        bundleView: action.payload.bundleView
          ? {
              categoryId: action.payload.bundleView.categoryId,
              reportSlug: action.payload.bundleView.reportSlug,
              toolbarSelection: {
                ...action.payload.bundleView.toolbarSelection,
              },
            }
          : undefined,
      }
      state.savedViews.unshift(row)
    },
    removeSavedView(state: ReportsState, action: PayloadAction<string>): void {
      state.savedViews = state.savedViews.filter((r) => r.id !== action.payload)
    },
    /**
     * Upsert a saved view by id. Used when editing presets (copy into Redux)
     * or updating a user-created saved view.
     */
    updateSavedView(
      state: ReportsState,
      action: PayloadAction<SavedViewRow>,
    ): void {
      const dateLabel = formatDateLabel(new Date())
      const next: SavedViewRow = {
        ...action.payload,
        name: action.payload.name.trim() || 'Untitled saved view',
        lastUpdated: dateLabel,
        preset: {
          ...action.payload.preset,
          categoryIds: [...action.payload.preset.categoryIds],
          metricIds: [...action.payload.preset.metricIds],
          toolbarSelection: { ...action.payload.preset.toolbarSelection },
        },
        bundleView: action.payload.bundleView
          ? {
              categoryId: action.payload.bundleView.categoryId,
              reportSlug: action.payload.bundleView.reportSlug,
              toolbarSelection: {
                ...action.payload.bundleView.toolbarSelection,
              },
            }
          : undefined,
      }
      const idx = state.savedViews.findIndex((r) => r.id === next.id)
      if (idx >= 0) {
        state.savedViews[idx] = next
      } else {
        state.savedViews.unshift(next)
      }
    },
  },
})

export const {
  addCustomReport,
  removeCustomReport,
  clearCustomReports,
  addScheduledReport,
  removeScheduledReport,
  updateScheduledReport,
  addSavedView,
  removeSavedView,
  updateSavedView,
} = reportsSlice.actions

export default reportsSlice.reducer
